import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";

import {
  useAddClientMutation,
  useUpdateClientMutation,
} from "src/api/sales/clients";
import { useGetCities, useGetCountries } from "src/api/generic";
import { useGetTermsList } from "src/api/sales/invoices";

const requiredIfVatExists = yup.string().when("vat", {
  is: (vat: string | undefined) => !!vat?.trim(),
  then: (schema) => schema.required("Required"),
  otherwise: (schema) => schema,
});

const schema = yup.object().shape({
  name: yup.string().required("Required"),
  phone: yup.string(),
  email: yup.string(),
  start_balance: yup.string(),
  bank_account: yup.string(),
  country_id: yup.string(),
  city_id: yup.string(),
  vat: yup.string(),
  street: requiredIfVatExists,
  building_number: requiredIfVatExists,
  zip_code: requiredIfVatExists,
  cib: yup.string(),
  agreements: yup.string(),
  terms: yup.array().of(yup.string()),
});

interface FormInputs extends yup.InferType<typeof schema> {}

interface IProps {
  open: boolean;
  handleClose: () => void;
  itemToUpdate?: any;
  refetch: Function;
}

const AddEditClientPopup = (props: IProps) => {
  const { open, handleClose, itemToUpdate, refetch } = props;
  const { t } = useTranslation("purchasing");
  const { t: salesT } = useTranslation("addSales");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  // APIS
  const { mutate, status, error, isPending } = useAddClientMutation();
  const {
    mutate: mutateUpdate,
    status: statusUpdate,
    error: errorUpdate,
    isPending: isPendingUpdate,
  } = useUpdateClientMutation();
  const { data: countriesData } = useGetCountries();
  const { data: citiesData } = useGetCities();
  const { data: termsData } = useGetTermsList();

  //React hook form
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    getValues
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      city_id: "1",
      country_id: "1",
      email: "",
      start_balance: "",
      bank_account: "",
      vat: "",
      street: "",
      building_number: "",
      zip_code: "",
      cib: "",
      agreements: "",
      terms: [],
    },
  });
  const mode = itemToUpdate ? "edit" : "add";

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const payload = { ...data, start_balance: data.start_balance || 0 };
    if (mode === "add") {
      mutate(payload);
    } else {
      mutateUpdate({
        id: itemToUpdate.id,
        ...payload,
      });
    }
  };

  useEffect(() => {
    if (itemToUpdate) {
      reset({
        name: itemToUpdate.name || "",
        phone: itemToUpdate.phone || "",
        email: itemToUpdate.email || "",
        start_balance: itemToUpdate.start_balance || "",
        bank_account: itemToUpdate.bank_account || "",
        vat: itemToUpdate.vat || "",
        country_id: itemToUpdate.country?.id || "",
        city_id: itemToUpdate.city?.id || "",
        street: itemToUpdate.street || "",
        building_number: itemToUpdate.building_number || "",
        zip_code: itemToUpdate.zip_code || "",
        cib: itemToUpdate.cib || "",
        agreements: itemToUpdate.agreements || "",
        terms: itemToUpdate.terms?.map((term: any) => term.id) || [],
      });
    }
  }, [itemToUpdate]);

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      refetch();
      handleClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      refetch();
      handleClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  console.log({ itemToUpdate }, getValues());

  return (
    <Modal
      open={open}
      // onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          minWidth: { xs: "100%", md: "537px" },
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,

          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"} lineHeight={"24px"}>
            {mode === "add" ? salesT("addClient") : salesT("editClient")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            {/* name */}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  required
                  type="string"
                  fullWidth
                  label={t("name")}
                  size="small"
                  {...field}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
            {/* bank account */}
            <Controller
              name="bank_account"
              control={control}
              render={({ field }) => (
                <TextField
                  type="string"
                  fullWidth
                  label={t("bankAccount")}
                  size="small"
                  {...field}
                  error={!!errors.bank_account}
                  helperText={errors.bank_account?.message}
                  InputLabelProps={{
                    shrink: field.value !== undefined,
                  }}
                />
              )}
            />
            {/*  contact number */}
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  fullWidth
                  label={salesT("contactNum")}
                  size="small"
                  {...field}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              )}
            />
            {/* direct_cost */}
            {/* <FormControl fullWidth size="small" error={!!errors.type}>
              <InputLabel id="demo-simple-select-label">
                {t("directIndirectCost")}
              </InputLabel>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    key={field.value}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label={t("directIndirectCost")}
                  >
                    <MenuItem value={"direct"}>{t("direct")}</MenuItem>
                    <MenuItem value={"indirect"}>{t("nonDc")}</MenuItem>
                  </Select>
                )}
              />
              <FormHelperText id="my-helper-text">
                {errors.type?.message}
              </FormHelperText>
            </FormControl> */}
            {/* min level */}
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  type="email"
                  fullWidth
                  label={generalT("email")}
                  size="small"
                  {...field}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />
            {/* start balance */}
            <Controller
              name="start_balance"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  fullWidth
                  label={salesT("start_balance")}
                  size="small"
                  {...field}
                  error={!!errors.start_balance}
                  helperText={errors.start_balance?.message}
                />
              )}
            />
            {/* vat */}
            <Controller
              name="vat"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  fullWidth
                  label={generalT("taxNumber")}
                  size="small"
                  {...field}
                  error={!!errors.vat}
                  helperText={errors.vat?.message}
                />
              )}
            />
            {/* street */}
            <Controller
              name="street"
              control={control}
              render={({ field }) => (
                <TextField
                  required={!!watch("vat")}
                  type="text"
                  fullWidth
                  label={salesT("street")}
                  size="small"
                  {...field}
                  error={!!errors.street}
                  helperText={errors.street?.message}
                />
              )}
            />
            {/* building number */}
            <Controller
              name="building_number"
              control={control}
              render={({ field }) => (
                <TextField
                  required={!!watch("vat")}
                  type="number"
                  fullWidth
                  label={salesT("buildingNum")}
                  size="small"
                  {...field}
                  error={!!errors.building_number}
                  helperText={errors.building_number?.message}
                />
              )}
            />

            {/* zip code */}
            <Controller
              name="zip_code"
              control={control}
              render={({ field }) => (
                <TextField
                  required={!!watch("vat")}
                  type="number"
                  fullWidth
                  label={salesT("zipCode")}
                  size="small"
                  {...field}
                  error={!!errors.zip_code}
                  helperText={errors.zip_code?.message}
                />
              )}
            />

            {/* cib */}
            <Controller
              name="cib"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  fullWidth
                  label={salesT("cib")}
                  size="small"
                  {...field}
                  error={!!errors.cib}
                  helperText={errors.cib?.message}
                />
              )}
            />
            {/* agreements */}
            <Controller
              name="agreements"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  fullWidth
                  label={salesT("agreements")}
                  size="small"
                  {...field}
                  error={!!errors.agreements}
                  helperText={errors.agreements?.message}
                />
              )}
            />
            {/* terms */}
            <FormControl fullWidth size="small" error={!!errors.terms}>
              <InputLabel id="terms-label">{salesT("terms")}</InputLabel>

              <Controller
                name="terms"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    multiple
                    labelId="terms-label"
                    id="terms"
                    label={salesT("terms")}
                    value={field.value || []}
                    sx={{
                      "& .MuiSelect-select": {
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        whiteSpace: "normal",
                        minHeight: "40px",
                      },
                    }}
                    renderValue={(selected) => (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                        }}
                      >
                        {(selected as (string | number)[]).map((id) => {
                          const term = termsData?.data?.find(
                            (term: any) => term.id === id,
                          );

                          return (
                            <Chip key={id} label={term?.title} size="small" />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {termsData?.data?.map(({ id, title }: any) => (
                      <MenuItem key={id} value={id}>
                        <Checkbox checked={(field.value || []).includes(id)} />
                        <ListItemText primary={title} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />

              <FormHelperText>
                {generalT(`${errors.terms?.message ?? ""}`)}
              </FormHelperText>
            </FormControl>
          </Stack>
          <Stack spacing={2} direction={"row"} mt={3}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
            >
              {t("cancel")}
            </Button>
            <LoadingButton
              variant="contained"
              fullWidth
              type="submit"
              loading={isPending || isPendingUpdate}
            >
              {t("save")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default AddEditClientPopup;
